import { Button, Modal, ModalActions, Select } from '@lokalise/louis';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';

import type { Language } from '@app/api/styleGuide';

import { useStyleGuideContext } from '../../../StyleGuideContext';
import { type FileUpload, isFileUploadRemoved, isFileUploadStaged } from '../../../model/fileUpload';
import StyleGuideGridRow from '../StyleGuideGridRow';
import { IconPlaceholder, StyleGuideGrid, StyleGuideGridHeaderRow, StyleGuideModalBody } from '../styles';

type AssignLanguageWizardProps = {
    fileUploads: FileUpload[];
    onUploadRemoved: (fileUpload: FileUpload) => void;
    onNameChanged: (fileUpload: FileUpload, name: string) => void;
    onLanguageAssigned: (fileUpload: FileUpload, language: Language | null) => void;
    onStyleGuidesSubmitted: () => Promise<{ data: void } | { error: FetchBaseQueryError | SerializedError } | null>;
    onBackToUploads: () => void;
    onCancel: () => void;
};

const AssignLanguageWizard = ({
    fileUploads,
    onUploadRemoved,
    onNameChanged,
    onLanguageAssigned,
    onBackToUploads,
    onStyleGuidesSubmitted,
    onCancel,
}: AssignLanguageWizardProps) => {
    const [submitting, setSubmitting] = useState(false);

    const { languages } = useStyleGuideContext();

    const submitStyleGuides = async () => {
        setSubmitting(true);
        await onStyleGuidesSubmitted();
    };

    const rows = useMemo(
        () =>
            fileUploads
                .filter((upload) => isFileUploadStaged(upload) || isFileUploadRemoved(upload))
                .map((staged) => (
                    <StyleGuideGridRow
                        key={staged.uploadId}
                        hidden={isFileUploadRemoved(staged)}
                        deletable={isFileUploadStaged(staged)}
                        name={isFileUploadStaged(staged) ? staged.name : staged.file.name}
                        onDelete={() => onUploadRemoved(staged)}
                        onNameChanged={(name) => onNameChanged(staged, name)}
                    >
                        {isFileUploadStaged(staged) && (
                            <Select
                                aria-label="Assign language"
                                value={staged.language}
                                options={languages}
                                getOptionLabel={(language) => language.label}
                                getOptionValue={(language) => `${language.id}`}
                                onChange={(language) => onLanguageAssigned(staged, language)}
                                menuPosition="fixed"
                            />
                        )}
                    </StyleGuideGridRow>
                )),
        [languages, fileUploads, onLanguageAssigned, onNameChanged, onUploadRemoved],
    );

    return (
        <Modal title="Upload style guides" opened onClose={onCancel}>
            <StyleGuideModalBody>
                <StyleGuideGrid>
                    <StyleGuideGridHeaderRow>
                        <IconPlaceholder />
                        <span>Name</span>
                        <span>Assign language</span>
                    </StyleGuideGridHeaderRow>

                    {rows}
                </StyleGuideGrid>
            </StyleGuideModalBody>

            <ModalActions>
                <Button variant="secondary" onClick={onBackToUploads}>
                    Upload more files
                </Button>
                <Button loading={submitting} variant="primary" onClick={submitStyleGuides}>
                    Save
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default AssignLanguageWizard;
